// GathrBot — GathrXR's little robot mascot (replaces the generic "AI sparkle").
//   variant="line"  → stroke + currentColor, for nav items / small inline use
//   variant="brand" → full-colour branded mascot (navy body, teal visor + aura),
//                     for hero / gallery moments. Pass `float` for a hover bob.
import React from 'react';
import './GathrBot.css';

interface GathrBotProps {
  size?: number;
  variant?: 'line' | 'brand';
  float?: boolean;
  className?: string;
  style?: React.CSSProperties;
  title?: string;
}

const GathrBot: React.FC<GathrBotProps> = ({ size = 20, variant = 'line', float = false, className = '', style, title }) => {
  const cls = `gathrbot ${float ? 'gathrbot--float' : ''} ${className}`.trim();

  if (variant === 'brand') {
    // Unique gradient ids per instance so multiple bots on a page don't clash.
    const uid = React.useId().replace(/:/g, '');
    return (
      <svg width={size} height={size} viewBox="0 0 48 48" className={cls} style={style} role="img" aria-label={title || 'GathrBot'}>
        {title ? <title>{title}</title> : null}
        <defs>
          <linearGradient id={`gb-body-${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#24487a" />
            <stop offset="1" stopColor="#0d1e35" />
          </linearGradient>
          <linearGradient id={`gb-visor-${uid}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#3fecdb" />
            <stop offset="1" stopColor="#00b1a3" />
          </linearGradient>
          <radialGradient id={`gb-aura-${uid}`} cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stopColor="#2ad4c4" stopOpacity="0.45" />
            <stop offset="1" stopColor="#2ad4c4" stopOpacity="0" />
          </radialGradient>
        </defs>
        {/* teal aura */}
        <ellipse cx="24" cy="25" rx="22" ry="20" fill={`url(#gb-aura-${uid})`} />
        {/* antenna */}
        <line x1="24" y1="11" x2="24" y2="6.5" stroke="#2ad4c4" strokeWidth="2" strokeLinecap="round" />
        <circle cx="24" cy="5" r="2.5" fill="#4bf1e1" />
        {/* ears */}
        <rect x="5.5" y="20" width="5" height="9" rx="2.5" fill={`url(#gb-body-${uid})`} />
        <rect x="37.5" y="20" width="5" height="9" rx="2.5" fill={`url(#gb-body-${uid})`} />
        <circle cx="8" cy="24.5" r="1.1" fill="#2ad4c4" />
        <circle cx="40" cy="24.5" r="1.1" fill="#2ad4c4" />
        {/* head */}
        <rect x="8.5" y="10.5" width="31" height="28" rx="12" fill={`url(#gb-body-${uid})`} stroke="#3fecdb" strokeOpacity="0.28" strokeWidth="1" />
        {/* top highlight */}
        <path d="M15 13.5 Q24 11.4 33 13.5" fill="none" stroke="#ffffff" strokeOpacity="0.18" strokeWidth="1.6" strokeLinecap="round" />
        {/* visor */}
        <rect x="13" y="16.5" width="22" height="14.5" rx="7.25" fill={`url(#gb-visor-${uid})`} />
        <rect x="13" y="16.5" width="22" height="14.5" rx="7.25" fill="none" stroke="#0a1a2e" strokeOpacity="0.15" strokeWidth="1" />
        {/* eyes */}
        <circle cx="19.5" cy="23.7" r="2.5" fill="#0a1626" />
        <circle cx="28.5" cy="23.7" r="2.5" fill="#0a1626" />
        <circle cx="20.4" cy="22.8" r="0.85" fill="#eafffb" />
        <circle cx="29.4" cy="22.8" r="0.85" fill="#eafffb" />
        {/* cheeks */}
        <circle cx="15.5" cy="27" r="1.15" fill="#2ad4c4" fillOpacity="0.5" />
        <circle cx="32.5" cy="27" r="1.15" fill="#2ad4c4" fillOpacity="0.5" />
        {/* smile */}
        <path d="M20 34 Q24 36.3 28 34" fill="none" stroke="#3fecdb" strokeOpacity="0.85" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7}
      strokeLinecap="round" strokeLinejoin="round" className={cls} style={style} role="img" aria-label={title || 'GathrBot'}>
      {title ? <title>{title}</title> : null}
      <line x1="12" y1="7.4" x2="12" y2="5" />
      <circle cx="12" cy="3.5" r="1.25" fill="currentColor" stroke="none" />
      <rect x="4.5" y="7.4" width="15" height="11.6" rx="4.4" />
      <path d="M4.5 12.6 H2.7" />
      <path d="M19.5 12.6 H21.3" />
      <circle cx="9.6" cy="12.6" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="14.4" cy="12.6" r="1.2" fill="currentColor" stroke="none" />
      <path d="M9.7 15.7 Q12 17.4 14.3 15.7" />
    </svg>
  );
};

export default GathrBot;
